import { errors } from "../constants";
import {
  createErrorResult,
  type IApiKeyDto,
  type ICreateApiKeyDto,
  type ICreatedApiKeyDto,
  type IRoleDropdownItem,
  type SimpleResult,
} from "../dto";
import { Api, simpleResultFromException } from "../helpers";

const API_KEYS_URL = "/api/v1/core/apiKeys";

// These routes answer with the backend's own SimpleResult, so its translated errors reach the hook unchanged.
// Only a failure before the service runs (403, network) arrives as an exception and is converted here.
export class ApiKeysApi {
  static async create(
    dto: ICreateApiKeyDto,
  ): Promise<SimpleResult<ICreatedApiKeyDto>> {
    try {
      const response = await Api.post<SimpleResult<ICreatedApiKeyDto>>(
        API_KEYS_URL,
        dto,
      );

      return (
        response?.data ?? createErrorResult(errors.common.emptyApiResponse)
      );
    } catch (error) {
      return simpleResultFromException({
        error,
        fallbackTranslationKey: errors.common.technicalProblemOccurred,
      });
    }
  }

  // Deleting goes through the generic drawer archive (DELETE), which also revokes; this keeps the row as history.
  static async revoke(id: number): Promise<SimpleResult<IApiKeyDto>> {
    try {
      const response = await Api.post<SimpleResult<IApiKeyDto>>(
        `${API_KEYS_URL}/${id}/revoke`,
      );

      return (
        response?.data ?? createErrorResult(errors.common.emptyApiResponse)
      );
    } catch (error) {
      return simpleResultFromException({
        error,
        fallbackTranslationKey: errors.common.technicalProblemOccurred,
      });
    }
  }

  // Roles a key of that company may hold; the backend applies the same rule when saving.
  static async getRoles(
    companyId: number,
  ): Promise<SimpleResult<IRoleDropdownItem[]>> {
    try {
      const response = await Api.get<SimpleResult<IRoleDropdownItem[]>>(
        `${API_KEYS_URL}/roles`,
        { companyId: String(companyId) },
      );

      return (
        response?.data ?? createErrorResult(errors.common.emptyApiResponse)
      );
    } catch (error) {
      return simpleResultFromException({
        error,
        fallbackTranslationKey: errors.common.technicalProblemOccurred,
      });
    }
  }
}
